import type {
  CodeableConcept,
  Practitioner,
  Reference,
  Task,
} from '@medplum/fhirtypes'
import { addBusinessDays, min } from 'date-fns'
import type { Order } from '#src/models/orders/index.ts'
import {
  SIGNATURE_TASK_CODES,
  SignatureTaskCode,
} from '#src/models/tasks/constants.ts'
import { StandardTask } from '#src/models/tasks/standard/standardTask.ts'
import { Role } from '#src/roles.ts'
import { type QueryTypes, queryTypesToString } from '#src/search.ts'
import { now } from '#src/utils.ts'

interface OrderSignatureTaskParams extends Partial<
  Omit<Task, 'resourceType' | 'requester'>
> {
  order: Order
  requester: Reference<Practitioner>
  owner?: Reference<Practitioner>
}

export class OrderSignatureTask extends StandardTask {
  static defaultRoles = [Role.PCP]

  constructor({ order, requester, ...rest }: OrderSignatureTaskParams) {
    super({
      code: OrderSignatureTask.code,
      focus: order.reference,
      for: order.participant,
      priority: order.urgent ? 'stat' : 'urgent',
      requester,
      ...rest,
    })
    this.title = 'Order Signature'
    this.description = 'Sign the order'
    this.state = 'todo'
    this.participant = order.participant
    let dueOn = order.urgent
      ? addBusinessDays(now(), 1).toISOString()
      : addBusinessDays(now(), 2).toISOString()
    if (order.dueDate) {
      dueOn = min([dueOn, order.dueDate]).toISOString()
    }
    this.dueOn = dueOn
  }

  static override get code(): CodeableConcept {
    return SIGNATURE_TASK_CODES[SignatureTaskCode.ORDER]
  }

  /**
   * Base FHIR search query for this resource — extended/modified at call sites.
   * Changing this affects every caller. Grep `baseSearchQuery` for impact before editing.
   */
  static baseSearchQuery(): QueryTypes {
    return {
      code: this.code,
    }
  }

  static searchQuery(order: Order): QueryTypes {
    return {
      focus: order.reference,
      code: this.code,
    }
  }

  static searchQueryString(order: Order): string {
    return queryTypesToString(this.searchQuery(order))
  }
}
